/**
 * Vigia de boot — torna mensurável a sessão que fica parada na tela de entrada
 *
 * ## O buraco que ele fecha
 *
 * Medido em 15/08/2026, iOS, 7 dias. Entre os devices que emitiram algum erro,
 * a chance de NUNCA ver o cardápio:
 *
 *     TypeError          5.156 devices   271 travados   5,3%
 *     ChunkLoadError     2.402 devices   119 travados   5,0%
 *     HttpErrorResponse  1.847 devices   116 travados   6,3%
 *
 * Só que a taxa geral de "não viu o cardápio" no iOS ficou **parada em 0,8-1,0%
 * o mês inteiro**, enquanto o `ChunkLoadError` variou 6× (276 → 1.772 → 59/dia).
 * Ou seja: os erros que sabemos medir explicam **cerca de um terço** das sessões
 * travadas. O resto — a maioria — trava sem emitir erro nenhum.
 *
 * E o pior caso nem entra nessa conta: um device que trava **antes** de emitir
 * qualquer evento não aparece nem no denominador. Hoje ele é invisível.
 *
 * Esse é exatamente o modo de falha do MP-569: "a página fica apenas
 * carregando", sem erro, sem log, sem nada. Sem este vigia, a única evidência
 * possível é o cliente ligando para o suporte.
 *
 * ## Como funciona
 *
 * O `main.ts` arma o vigia ANTES do `bootstrapModule`. Se o app não avisar que
 * ficou pronto dentro do prazo, o vigia reporta `ClientBootStalled` com a FASE
 * em que parou. A fase é o que transforma "travou" em diagnóstico:
 *
 *     'cardapio'         → o APP_INITIALIZER do cardápio não voltou. É o caso em
 *                          que o Angular nem faz o bootstrap e o esqueleto do
 *                          index.html ("Carregando o cardápio…") fica na tela.
 *     'bootstrap'        → o initializer voltou e o Angular ainda não montou.
 *     'primeira-tela'    → o app montou e a navegação de entrada nem foi PEDIDA.
 *     'navegacao-pedida' → o `navService.push` FOI chamado e a promessa dele
 *                          continua em voo. Ver abaixo por que essa fase existe.
 *
 * ## Por que 'navegacao-pedida' foi criada (01/09/2026)
 *
 * Medido no Athena em 31/08/2026: 328 a 471 devices/dia batem `ClientBootStalled`
 * na fase 'primeira-tela', e ~71% deles NUNCA veem o cardápio. Cruzando por
 * `device_id`, dos 471 travados só 14 (3%) emitiram `ClientAppError`: os 334 que
 * não viram o menu não emitiram erro NENHUM. Silêncio total é a assinatura de
 * promessa que nunca assenta — uma que rejeitasse produziria erro.
 *
 * O candidato é `app.component.ts agendarNavegacaoDeBoot()`:
 *
 *     this.navService.push(routeTo, params).then((navegou) => { ... });
 *
 * sem `.catch()` e sem prazo, sobre um `router.navigateByUrl` igualmente nu. Se
 * essa promessa pendurar, `marcarBootPronto()` nunca roda — e o
 * `loadingSpinner.dismiss()` do início do `ngOnInit` já rodou, então o spinner
 * sai e nada entra no lugar: tela literalmente branca, não splash eterno.
 *
 * Só que a fase 'primeira-tela' cobre DOIS estados que pedem curas opostas —
 * "montou e nem chegou a pedir navegação" (algo antes do push travou: o `await`
 * do último pedido, o `setTimeout` de 500 ms, uma exceção no meio do `ngOnInit`)
 * e "pediu e a promessa pendurou" (o defeito acima). Sem separar os dois, medir
 * o efeito de qualquer correção fica impossível: os dois números somam no mesmo
 * balde. `'navegacao-pedida'` é essa fronteira, e é a única lacuna do boot que
 * hoje não tem medição nenhuma.
 *
 * ## Por que ele não usa a injeção de dependência
 *
 * Porque o cenário que ele mede é justamente **o Angular não ter subido**. Um
 * serviço injetável nunca seria construído nesse caso. Por isso aqui é estado de
 * módulo, `sendBeacon` cru e leitura direta do `localStorage`.
 *
 * Pelo mesmo motivo ele NÃO passa pela allowlist do `order-flow-steps.ts`, que
 * é aplicada dentro do `OrderFlowTraceService`. A proteção equivalente é de
 * construção: **todo campo do payload abaixo é constante, número medido aqui
 * dentro, ou string SANITIZADA aqui dentro** — nenhum dado do cliente tem
 * caminho até ele. A única string que vem de fora é a rota de boot, e ela passa
 * por `sanitizarRota()` (ver lá: só nomes de rota sobrevivem, token nenhum).
 *
 * ## O que ele passou a cobrir (01/09/2026)
 *
 * - **QUEM trava**: o payload agora leva `user_agent`, com o MESMO
 *   `.slice(0, 200)` do `OrderFlowTraceService` e da sonda inline, para as três
 *   fontes cruzarem no Athena. Sem ele, os 328-471 travados/dia eram um
 *   agregado mudo: não dava para dizer se são o mesmo público do boot morto
 *   (medido 100% iOS 15/Safari) ou outro completamente diferente — e a
 *   diferença decide se são uma frente ou duas.
 * - **QUANDO trava de verdade**: `ms_nav` é `performance.now()` no envio. O
 *   campo `ms` é a CONSTANTE do prazo (20000) e por isso não diz nada sobre o
 *   tempo real de vida do documento; `ms_nav` diz, e é comparável com o `ms`
 *   MEDIDO do `ClientBootAbandoned`. Um `ms_nav` muito acima do prazo é vigia
 *   armado tarde (bundle que demorou a chegar), não app travado — a distinção
 *   que hoje não existe.
 * - **PARA ONDE ia**: `rota_alvo` é o `routeTo` do `agendarNavegacaoDeBoot`.
 *   `/menu` travando é um defeito; `rate-order` ou `/shopping-cart` travando é
 *   outro (só clientes recorrentes chegam neles, e são justamente os caminhos
 *   que dependem de rede antes de navegar).
 *
 * ## O que ele ainda NÃO cobre
 *
 * - **O `main.js` que nunca chega.** Se o bundle não executa, este código não
 *   roda. Isso hoje é medido por FORA, pela sonda inline no fim do `index.html`
 *   (`ClientBootNoScript` / `ClientBootAbandoned`), que existe justamente
 *   porque nada dentro do bundle poderia medi-la. As duas fontes vivem em
 *   escalas diferentes e não se substituem: 3-11 devices/dia no boot morto
 *   contra 328-471 aqui (01/09/2026).
 * - **A CAUSA de a promessa não assentar.** Este vigia diz que a navegação foi
 *   pedida e não voltou; não diz por quê (guard pendurado, resolver esperando
 *   rede, chunk da rota em trânsito morto). Descobrir isso exige instrumentar o
 *   `NavService`/os guards, não este arquivo.
 * - **Travamento DEPOIS da primeira tela.** É um vigia de BOOT: `marcarBootPronto()`
 *   o desarma para sempre. Uma navegação que pendura no meio da sessão (o mesmo
 *   `router.navigateByUrl` nu, no checkout) continua invisível.
 * - **O device que fecha a aba antes do prazo.** Com `PRAZO_VIGIA_MS = 20000`,
 *   quem desiste em 15 s não é contado. É o mesmo viés já medido na sonda
 *   inline (`levapizza` recarregava a cada ~22 s e tem ZERO evento): os números
 *   daqui são PISO, não taxa.
 */

/**
 * Onde o boot estava quando o prazo estourou. A ordem aqui é a ordem real do
 * boot, e 'navegacao-pedida' entra ENTRE 'primeira-tela' e 'pronto' porque é
 * exatamente o intervalo entre chamar `navService.push` e a promessa dele
 * assentar — o intervalo em que o candidato a causa da tela branca mora.
 */
export type FaseDoBoot =
  | 'cardapio'
  | 'bootstrap'
  | 'primeira-tela'
  | 'navegacao-pedida'
  | 'pronto';

/**
 * Prazo do vigia. Generoso de propósito: em 3G ruim um boot honesto passa dos
 * 10 s, e um falso positivo aqui envenenaria a métrica que estamos criando.
 * O que queremos pegar é a tela parada, não a tela lenta.
 */
export const PRAZO_VIGIA_MS = 20000;

/** Mesma chave do `AddressCaptureSessionService`, para o device casar entre os dois. */
export const CHAVE_DEVICE_ID = 'geocode_device_id';

/** Nome do evento. Registrado também em `order-flow-steps.ts`, para achabilidade. */
export const PASSO_TRAVOU = 'ClientBootStalled';

/**
 * Rota que o boot informou como destino, quando ele chegou a informar. É o
 * valor JÁ SANITIZADO — nada que veio de fora fica guardado cru.
 */
const ROTA_IRRECONHECIVEL = '(outra)';

/** Tamanho máximo do `rota_alvo` no payload. */
const MAX_ROTA = 40;

/**
 * Só nome de rota passa: letras e hífen, no máximo 20 caracteres, sem dígito
 * nenhum. É deliberadamente restritivo porque o `routeTo` do `app.component`
 * pode ser `currentRoute.join('/')` — ou seja, o CAMINHO DA URL do cliente, que
 * nos fluxos `track` / `rate-order` / `reset-password` carrega TOKEN. As rotas
 * de verdade ('menu', 'shopping-cart', 'rate-order', 'track', 'reset-password')
 * passam; qualquer identificador (hex, uuid, id numérico) cai fora por conter
 * dígito ou por ser longo demais.
 */
const RE_SEGMENTO_DE_ROTA = /^[a-zA-Z][a-zA-Z-]{0,19}$/;

let fase: FaseDoBoot = 'cardapio';
let rotaAlvo = '';
let pronto = false;
let timer: any = null;

/** Marca em que ponto do boot estamos. Barato e idempotente. */
export function marcarFaseDoBoot(f: FaseDoBoot): void {
  if (!pronto) fase = f;
}

export function faseDoBoot(): FaseDoBoot {
  return fase;
}

/**
 * Descarta query string, fragmento e qualquer segmento que não seja nome de
 * rota. Devolve `''` quando não há o que reportar (aí o campo simplesmente não
 * é enviado) e `'(outra)'` quando a rota FOI informada mas nada sobreviveu — a
 * diferença importa: campo ausente significa que a navegação nunca foi pedida,
 * e é essa ausência que confirma um travamento ANTES do `push`.
 */
function sanitizarRota(rota: string): string {
  try {
    if (typeof rota !== 'string' || !rota) return '';
    const partes = rota
      .split('#')[0]
      .split('?')[0]
      .split('/')
      .filter((p) => RE_SEGMENTO_DE_ROTA.test(p));
    if (!partes.length) return ROTA_IRRECONHECIVEL;
    return ('/' + partes.join('/')).slice(0, MAX_ROTA);
  } catch (e) {
    return ''; // string exótica: não reportar é melhor que reportar errado
  }
}

/**
 * Registra o destino da navegação de boot. A API é essa — uma string, sem
 * objeto e sem params — de propósito: `params` do `agendarNavegacaoDeBoot`
 * carrega o ÚLTIMO PEDIDO do cliente (`{'order': response, ...}`), e nada disso
 * pode ter caminho até um beacon. Aqui só entra a rota, e já sanitizada.
 */
export function marcarRotaDeBoot(rota: string): void {
  if (pronto) return;
  rotaAlvo = sanitizarRota(rota);
}

/** Só para leitura/teste: a rota como ela iria no payload. */
export function rotaDeBoot(): string {
  return rotaAlvo;
}

/**
 * O app chegou à primeira tela. Desarma o vigia — depois disto ele nunca mais
 * reporta, mesmo que algo volte a travar (o vigia é de BOOT, não de sessão).
 */
export function marcarBootPronto(): void {
  pronto = true;
  fase = 'pronto';
  if (timer !== null) {
    try { clearTimeout(timer); } catch (e) { /* timer exótico */ }
    timer = null;
  }
}

export function bootEstaPronto(): boolean {
  return pronto;
}

/** Lê (ou cria) o id do device, com a MESMA chave que o resto da telemetria usa. */
function idDoDevice(): string {
  try {
    const existente = localStorage.getItem(CHAVE_DEVICE_ID);
    if (existente) return existente;
    // Criar aqui é de propósito: sem id, um device travado só poderia ser
    // contado como evento, e a régua do funil é DEVICE. Gravar na mesma chave
    // faz a sessão seguinte do mesmo aparelho reaproveitar o id.
    const novo = novoId();
    localStorage.setItem(CHAVE_DEVICE_ID, novo);
    return novo;
  } catch (e) {
    return ''; // storage bloqueado (modo privado): evento sem device
  }
}

function novoId(): string {
  try {
    const c: any = (typeof crypto !== 'undefined') ? crypto : null;
    if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  } catch (e) { /* segue para o fallback */ }
  return 'w-' + String(Math.floor(Date.now())) + '-' +
    String(Math.floor(Math.random() * 1e9));
}

/**
 * Milissegundos desde o início da navegação, no momento do envio.
 *
 * Devolve `null` quando o relógio não está ao alcance, e aí o campo NÃO é
 * enviado: ausência é informação (mesma postura do `client_code_version` na
 * sonda inline), enquanto um `0` inventado entraria na média e a estragaria.
 */
function msDesdeANavegacao(): number | null {
  try {
    if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
      const t = performance.now();
      if (typeof t === 'number' && isFinite(t)) return Math.round(t);
    }
  } catch (e) { /* performance exótico/bloqueado */ }
  return null;
}

/** Contexto do device. Tudo medido aqui; nada vem do cliente. */
function contexto(): { [k: string]: any } {
  const d: { [k: string]: any } = {};
  try {
    // A PLATAFORMA, primeiro campo — pelo mesmo motivo da sonda inline. Até
    // 01/09/2026 este passo NÃO coletava user_agent, então os 328-471
    // travados/dia eram um número sem dono: não dava para saber se são o mesmo
    // público do boot morto (100% iOS 15/Safari) ou outro. Mesmo
    // `.slice(0, 200)` do `OrderFlowTraceService` e da sonda, para as três
    // fontes cruzarem no Athena sem normalização.
    const ua = (typeof navigator !== 'undefined' && navigator)
      ? (navigator.userAgent || '') : '';
    if (ua) d.user_agent = ua.slice(0, 200);
  } catch (e) { /* navigator ausente/bloqueado: o passo vale sem plataforma */ }
  try {
    const sw: any = (navigator as any).serviceWorker;
    if (sw) d.sw_controlled = !!sw.controller;
  } catch (e) { /* contexto sem serviceWorker */ }
  try {
    const conexao: any = (navigator as any).connection;
    if (conexao && typeof conexao.effectiveType === 'string') {
      d.net_type = conexao.effectiveType.slice(0, 10);
    }
  } catch (e) { /* Chromium-only; ausência é sinal */ }
  try {
    if (typeof matchMedia === 'function' &&
        matchMedia('(display-mode: standalone)').matches) {
      d.display_mode = 'standalone';
    } else if ((navigator as any).standalone) {
      d.display_mode = 'standalone';
    } else {
      d.display_mode = 'browser';
    }
  } catch (e) { /* sem matchMedia */ }
  try {
    const nav: any = (typeof performance !== 'undefined' && performance.getEntriesByType)
      ? performance.getEntriesByType('navigation')[0] : null;
    if (nav) {
      if (typeof nav.workerStart === 'number') d.doc_from_sw = nav.workerStart > 0;
      if (typeof nav.type === 'string') d.nav_type = nav.type;
    }
  } catch (e) { /* Navigation Timing ausente */ }
  return d;
}

/** `ts` no formato que o Firehose/Glue espera (o mesmo de `hiveTs`). */
function carimbo(): string {
  return new Date().toISOString().slice(0, 19).replace('T', ' ');
}

export interface OpcoesVigia {
  /** Endpoint de ingestão. Vazio desliga o vigia (é o caso de green/blue). */
  url: string;
  /** Versão do código, para parear com o resto da telemetria. */
  clientCodeVersion?: string;
  prazoMs?: number;
  /** Injeções só para teste. */
  agendar?: (fn: () => void, ms: number) => any;
  enviar?: (url: string, envelope: any) => void;
}

/** Envio cru: `sendBeacon` sobrevive à navegação; `fetch` com keepalive é o reserva. */
function enviarPadrao(url: string, envelope: any): void {
  const corpo = JSON.stringify(envelope);
  try {
    const nav: any = navigator;
    if (nav && typeof nav.sendBeacon === 'function') {
      const blob = new Blob([corpo], { type: 'application/json' });
      if (nav.sendBeacon(url, blob)) return;
    }
  } catch (e) { /* cai no fetch */ }
  try {
    fetch(url, { method: 'POST', body: corpo, keepalive: true,
                 headers: { 'Content-Type': 'application/json' } })
      .catch(() => undefined);
  } catch (e) { /* sem rede: o evento se perde, e tudo bem */ }
}

/**
 * Arma o vigia. Chamar mais de uma vez não empilha timers.
 *
 * Nunca lança: um defeito aqui não pode ser o motivo de o app não subir — seria
 * o instrumento causando a doença que veio medir.
 */
export function armarVigiaDeBoot(opcoes: OpcoesVigia): boolean {
  try {
    if (!opcoes || !opcoes.url) return false;
    if (timer !== null || pronto) return false;

    const prazo = (typeof opcoes.prazoMs === 'number' && opcoes.prazoMs > 0)
      ? opcoes.prazoMs : PRAZO_VIGIA_MS;
    const agendar = opcoes.agendar || ((fn: () => void, ms: number) => setTimeout(fn, ms));
    const enviar = opcoes.enviar || enviarPadrao;

    timer = agendar(() => {
      timer = null;
      try {
        if (pronto) return;
        const dados: any = contexto();
        dados.fase = fase;
        dados.ms = prazo;
        // Tempo REAL de vida do documento. `ms` acima é a constante do prazo e
        // não distingue "o app travou 20 s" de "o vigia só foi armado aos 40 s
        // porque o bundle demorou a chegar" — os dois viram o mesmo evento.
        const msNav = msDesdeANavegacao();
        if (msNav !== null) dados.ms_nav = msNav;
        // Ausente = a navegação de boot nunca foi PEDIDA. Combinado com a fase,
        // é o que separa os dois defeitos que hoje somam no mesmo balde.
        if (rotaAlvo) dados.rota_alvo = rotaAlvo;
        dados.device_id = idDoDevice();
        if (opcoes.clientCodeVersion) dados.client_code_version = opcoes.clientCodeVersion;
        enviar(opcoes.url, {
          ts: carimbo(),
          source: 'pedirdelivery-client',
          step: PASSO_TRAVOU,
          order_uuid: '',
          order_id: '0',
          // O restaurante ainda não carregou — é justamente o que travou. '0'
          // mantém o campo com o mesmo tipo do resto da tabela.
          restaurant_id: '0',
          data: JSON.stringify(dados),
        });
      } catch (e) { /* reportar não pode quebrar nada */ }
    }, prazo);

    return true;
  } catch (e) {
    return false;
  }
}

/** Só para teste: devolve o módulo ao estado inicial. */
export function _resetarVigiaParaTeste(): void {
  fase = 'cardapio';
  rotaAlvo = '';
  pronto = false;
  if (timer !== null) { try { clearTimeout(timer); } catch (e) { /* */ } }
  timer = null;
}
